/**
 * "Create workflow definition" dropdown button used on the Workflows page.
 *
 * Styled to match the Knowledge Base "Add Document" button: a single primary
 * Button that opens a Headless UI Menu with two choices:
 *   - Create new workflow definition → fresh empty draft, navigate to builder
 *   - Start from template           → opens a Dialog that lists templates and
 *                                     seeds a new draft from the chosen one
 *
 * Drafts are persisted client-side for v1; navigation kicks the user into the
 * builder at `/app/workflows/builder/<id>`.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ChevronDownIcon,
  DocumentPlusIcon,
  PlusIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';

import { createEmptyState } from './builder/state/reducer';
import { saveDraft } from './builder/state/persistence';
import { TEMPLATES } from './builder/fixtures/templates';

export function CreateWorkflowDefinitionButton() {
  const navigate = useNavigate();
  const [templateDialogOpen, setTemplateDialogOpen] = React.useState(false);

  const createBlank = () => {
    const fresh = createEmptyState();
    saveDraft(fresh);
    navigate(`/app/workflows/builder/${fresh.id}`);
  };

  const createFromTemplate = (templateId: string) => {
    const template = TEMPLATES.find((t) => t.id === templateId);
    if (!template) return;
    const fresh = {
      ...createEmptyState(template.label),
      description: template.description,
      inputs: template.inputs,
      nodes: template.nodes
    };
    saveDraft(fresh);
    setTemplateDialogOpen(false);
    navigate(`/app/workflows/builder/${fresh.id}`);
  };

  return (
    <>
      <Menu as="div" className="relative">
        <MenuButton as={Button} color="sky">
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New workflow definition
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        </MenuButton>
        <MenuItems
          transition
          className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          <div className="p-1">
            <MenuItem>
              <button
                type="button"
                onClick={createBlank}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10"
              >
                <DocumentPlusIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                From scratch
              </button>
            </MenuItem>
            <MenuItem>
              <button
                type="button"
                onClick={() => setTemplateDialogOpen(true)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10"
              >
                <RectangleStackIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                From template
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </Menu>

      <TemplatePickerDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelect={createFromTemplate}
      />
    </>
  );
}

function TemplatePickerDialog(props: {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => void;
}) {
  return (
    <Dialog open={props.open} onClose={props.onClose} size="2xl">
      <DialogTitle>Start from a template</DialogTitle>
      <DialogDescription>
        Templates seed a new draft pre-wired with inputs and steps. You can customize everything
        after it opens in the builder.
      </DialogDescription>
      <DialogBody>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {TEMPLATES.map((tpl) => (
            <li key={tpl.id}>
              <button
                type="button"
                onClick={() => props.onSelect(tpl.id)}
                className="group flex h-full w-full flex-col items-start gap-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 text-left transition hover:border-sky-400 hover:bg-sky-50/40 dark:hover:bg-sky-500/10"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 ring-1 ring-inset ring-sky-200 dark:ring-sky-500/30 group-hover:bg-sky-100 dark:group-hover:bg-sky-500/20">
                    <RectangleStackIcon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {tpl.label}
                  </span>
                </div>
                <p className="text-xs leading-snug text-neutral-500 dark:text-neutral-400">
                  {tpl.description}
                </p>
                <div className="mt-auto pt-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
                  {tpl.nodes.length} {tpl.nodes.length === 1 ? 'step' : 'steps'} ·{' '}
                  {tpl.inputs.length} {tpl.inputs.length === 1 ? 'input' : 'inputs'}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={props.onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
